import {Autocomplete, Box, FormLabel, Stack, Typography} from "@mui/material";
import React, {FC, useEffect, useMemo} from "react";
import * as z from "zod";
import {Controller, FormProvider, useFieldArray, useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import TextField from "@mui/material/TextField";
import {useGlobalActions} from "@/store/global.c";
import {QueryObserverResult, RefetchOptions} from "@tanstack/react-query";
import {DialogFooter} from "@/widget/DialogModal/DialogModal";
import {RoleUserVO} from "@/service/user.s";
import {useRoleUsers} from "@/store/user.c";
import {TitleBar} from "@/widget/TitleBar/TitleBar";
import Chip from "@mui/material/Chip";
import {ControlPointOutlined, RemoveCircleOutlineOutlined} from "@mui/icons-material";
import {Grid, IconButton} from "@mui/material";
import {useSearchProduct, useAssignProduct} from "@/service/contract.s";
import {ProductPO} from "@/model/product";
import FormControl from "@mui/material/FormControl";
import {DatePickers} from "@/theme/DatePickers";
import DatePicker from "react-datepicker";
import {iso2Date} from "@/helper/method";
import {isNumber} from "lodash";

export interface AssignProductProps {
    instanceId: number;
    businessId?: number;
    executeId: string;
    productTypeId: number
    refetchExecutes: (options?: RefetchOptions) => Promise<QueryObserverResult>
}

const assignItemsSchema = z.array(z.object({
    num: z.number({message: "请填写分配数量"}),
    writer: z.unknown(),
    writerId: z.number({message: "请选择撰稿人"}),
    handler: z.unknown(),
    handlerId: z.unknown(),
    insideDeadline: z.any(),
    tempHandler: z.object({id: z.number(), username: z.string(),}).optional().nullable(),
    tempWrite: z.object({id: z.number(), username: z.string(),}).optional(),
    id: z.string().optional(),
    productTypeId: z.unknown()
}).superRefine((values, ctx) => {
    if (values.productTypeId != 2) {
        if (!isNumber(values.handlerId)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: '请选择处理人',
                path: ['handlerId'],
            });
        }

    }
}))

const productItemsSchema = z.array(z.object({
    id: z.string(),
    num: z.number(),
    category: z.string(),
    assignItems: assignItemsSchema
}))

const validationSchema = z.object({
    instanceId: z.number(),
    businessId: z.number(),
    executeId: z.string(),
    productItems: productItemsSchema
});

export type AssignProductForm = z.infer<typeof validationSchema>
export type AssignItemsForm = z.infer<typeof assignItemsSchema>

export type AssignItemsProps = {
    fieldIndex: number
    category: string
    num: number
}

export const AssignProduct: FC<AssignProductProps> = ({productTypeId, instanceId, executeId, refetchExecutes, businessId}) => {
    const {mutate: searchProductMutate, data: productData,} = useSearchProduct()
    const initAssignProduct = (products?: ProductPO[]) => {
        return {
            executeId, instanceId, businessId, productItems:
                products?.map(item => ({
                    category: item.category,
                    id: item.id,
                    num: item.num,
                    assignItems: [{num: item.num, productTypeId, insideDeadline: iso2Date(item.insideDeadline)}]
                }))
        } as AssignProductForm
    }

    const form = useForm<AssignProductForm>({
        resolver: zodResolver(validationSchema),
        values: initAssignProduct(productData?.data),
    });
    const {
        handleSubmit,
        reset,
        control,
        formState: {errors},
    } = form

    useEffect(() => {
        if (businessId) {
            searchProductMutate({id: businessId, productTypeIds: productTypeId})
        }
    }, [businessId]);

    const {mutateAsync: assignProductMutate, isPending: assignProductPending} = useAssignProduct()
    const {setToast} = useGlobalActions()
    const {setDialogModal} = useGlobalActions()

    const {fields: productItemFields} = useFieldArray({
        name: "productItems",
        control,
    });

    const roles = useMemo(() => {
        let roles: string[] = []
        if (productTypeId == 1) {
            roles = ["patenter", "processer"]
        } else if (productTypeId == 2) {
            roles = ["declarer", "manager"]
        } else if (productTypeId == 3 || productTypeId == 4) {
            roles = ["processer"]
        }
        return roles
    }, [productTypeId]);

    const writers: RoleUserVO[] = useRoleUsers(roles)
    const handlers: RoleUserVO[] = useRoleUsers(["processer"])

    const onSubmit = async (values: AssignProductForm) => {
        const productItems = values.productItems
        let errNum: boolean = false
        let newAssignItems: AssignItemsForm = []
        productItems.map((productItem) => {
            const assignItems = productItem.assignItems
            const num = productItem.num
            const submitAssignItems: AssignItemsForm = []
            const totalNum = assignItems.reduce((prev: number, next) => {
                submitAssignItems.push({...next, id: productItem.id?.toString()})
                return prev + next.num
            }, 0)
            if (totalNum != num) {
                errNum = true
                return
            }
            newAssignItems = newAssignItems.concat(submitAssignItems)
        })
        if (errNum) {
            setToast({message: "请检查分配数量", open: true, severity: 'error', duration: 4000});
            return
        }
        const result = await assignProductMutate({...values, assignItems: newAssignItems, productTypeId})
        if (result.code == 0) {
            setToast({message: result.msg, severity: 'success', open: true, duration: 2000});
            await refetchExecutes()
            setDialogModal({open: false})
        } else {
            setToast({message: result.msg, open: true, severity: 'error', duration: 2000});
        }
    }
    const AssignItems = ({fieldIndex, category, num}: AssignItemsProps) => {
        const {
            fields: assignItemFields, append: appendAssignItem,
            remove: removeAssignItem, update
        } = useFieldArray<AssignProductForm>({
            name: `productItems.${fieldIndex}.assignItems`,
            control
        })
        return <> <TitleBar key={fieldIndex} title={<Stack alignItems={'center'} spacing={0.8} direction={'row'}>
            <Chip variant={'outlined'} color={'info'} label={num} size={'small'}/><Typography variant={"h6"} fontSize={16} fontWeight={500}>
            {category}
        </Typography></Stack>} extra={<IconButton size={"small"} onClick={() => appendAssignItem({num: 1, writer: "", productTypeId} as any)} color={'default'} sx={{padding: 0}}>
            <ControlPointOutlined/>
        </IconButton>}/>
            {assignItemFields.map((field, index) => {
                return <Grid key={field.id} width={'100%'} spacing={2} container={true}>
                    <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel>撰稿人</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => {
                                return <Autocomplete
                                    options={writers}
                                    value={value}
                                    onChange={(_, value: RoleUserVO | null) => {
                                        update(index, {...field, writerId: value?.id || undefined as any, writer: value?.username || ""})
                                        onChange(value)
                                    }}
                                    isOptionEqualToValue={(option, value) => option.id == value.id}
                                    getOptionKey={option => option.username}
                                    getOptionLabel={option => option.username}
                                    fullWidth
                                    renderInput={(params) => (
                                        <TextField
                                            error={!!errors?.productItems?.[fieldIndex]?.assignItems?.[index]?.writerId}
                                            helperText={errors?.productItems?.[fieldIndex]?.assignItems?.[index]?.writerId?.message}
                                            variant="outlined"
                                            placeholder={"选择撰稿人"} {...params} />
                                    )}
                                />
                            }} name={`productItems.${fieldIndex}.assignItems.${index}.tempWrite`}/>
                        </FormControl>
                    </Grid>
                    <Grid size={assignItemFields.length > 1 ? 4.8 : 6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel>数量</FormLabel>
                            <Controller render={({field: {onChange, value}}) => {
                                return <TextField
                                    value={value}
                                    onChange={(event) => onChange(parseInt(event.target.value || ""))}
                                    error={!!errors?.productItems?.[fieldIndex]?.assignItems?.[index]?.num}
                                    type={'number'}
                                    helperText={errors?.productItems?.[fieldIndex]?.assignItems?.[index]?.num?.message}
                                    placeholder="撰写数量"
                                    fullWidth
                                />
                            }} name={`productItems.${fieldIndex}.assignItems.${index}.num`}/>
                        </FormControl>
                    </Grid>
                    {productTypeId != 2 && <Grid size={6}>
                        <FormControl fullWidth={true} required={true}>
                            <FormLabel>处理人</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => {
                                return <Autocomplete
                                    options={handlers}
                                    value={value}
                                    onChange={(_, value: RoleUserVO | null) => {
                                        update(index, {...field, handlerId: value?.id || undefined as any, handler: value?.username || ""})
                                        onChange(value)
                                    }}
                                    isOptionEqualToValue={(option, value) => option.id == value.id}
                                    getOptionKey={option => option.username}
                                    getOptionLabel={option => option.username}
                                    fullWidth
                                    renderInput={(params) => (
                                        <TextField
                                            error={!!errors?.productItems?.[fieldIndex]?.assignItems?.[index]?.handlerId}
                                            helperText={errors?.productItems?.[fieldIndex]?.assignItems?.[index]?.handlerId?.message}
                                            variant="outlined"
                                            placeholder={"选择处理人"} {...params} />
                                    )}
                                />
                            }} name={`productItems.${fieldIndex}.assignItems.${index}.tempHandler`}/>
                        </FormControl>
                    </Grid>}
                    <Grid size={assignItemFields.length > 1 ? productTypeId == 2 ? 10.8 : 4.8 : productTypeId == 2 ? 12 : 6}>
                        <FormControl fullWidth={true} autoFocus={true} required={true}>
                            <FormLabel>内部期限</FormLabel>
                            <Controller control={control} render={({field: {onChange, value}}) => <DatePickers>
                                <DatePicker
                                    showYearDropdown={true}
                                    showMonthDropdown={true}
                                    dateFormat="yyyy/MM/dd"
                                    selected={value}
                                    onChange={date => onChange(date)}
                                    locale='zhCN'
                                    placeholderText={"选择内部期限"}
                                    customInput={<TextField
                                        // error={!!errors?.productItems?.[fieldIndex]?.assignItems?.[index]?.insideDeadline}
                                        // helperText={errors?.productItems?.[fieldIndex]?.assignItems?.[index]?.insideDeadline?.message}
                                        sx={{
                                            width: "100%",
                                            "& .MuiInputBase-root": {padding: "8px 8px 8px 12px"},
                                        }}
                                    />}
                                />
                            </DatePickers>} name={`productItems.${fieldIndex}.assignItems.${index}.insideDeadline`}/>
                        </FormControl>
                    </Grid>
                    {assignItemFields.length > 1 && <Grid size={1.2} textAlign={'right'} pr={'4px'} alignContent={'center'}>
                        <IconButton
                            size={"small"}
                            onClick={() => removeAssignItem(index)}
                            color={'error'}
                            sx={{padding: 0}}
                        >
                            <RemoveCircleOutlineOutlined/>
                        </IconButton>
                    </Grid>}

                </Grid>
            })}</>
    }

    return <FormProvider {...form}>
        <Box component={'form'} display={'flex'} flexDirection={'column'} rowGap={2} noValidate onSubmit={handleSubmit(onSubmit)}>
            {productItemFields.map((field, index) => {
                return (<Grid key={field.id} id={field.id}
                              spacing={2} container width={'inherit'}>
                        <AssignItems category={field.category} num={field.num} fieldIndex={index}/>
                    </Grid>
                )
            })}
            <DialogFooter onReset={() => reset(initAssignProduct(productData?.data))} loading={assignProductPending}/>
        </Box>
    </FormProvider>

}